import { Button } from "@/components/ui/button";
import {
  FpDropdownMenu,
  FpDropdownMenuContent,
  FpDropdownMenuItem,
  FpDropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useHandler } from "@/hooks";
import { useActiveProjectId } from "@/lib/hooks/useActiveProjectId";
import { useActiveSnapshotId } from "@/lib/hooks/useActiveSnapshot";
import { useSortedSnapshots } from "@/lib/hooks/useActiveSnapshot";
import type { UiSnapshotSummary } from "@/state/stores/types";
import { useNavigate } from "@tanstack/react-router";
import { ChevronDownIcon } from "lucide-react";
import { useMemo } from "react";

export function VersionDropdown() {
  const activeSnapshotId = useActiveSnapshotId();
  const navigate = useNavigate();
  const projectId = useActiveProjectId();
  const onSnapshotSelect = useHandler((snapshotId: string) => {
    if (snapshotId === activeSnapshotId) {
      return;
    }

    if (!projectId) {
      console.error("No project ID available");
      return;
    }

    let tab: "code" | "spec" | "preview" = "spec";
    try {
      const parsedTab = new URLSearchParams(window.location.search).get("tab");
      switch (parsedTab) {
        case "code":
          tab = "code";
          break;
        case "preview":
          tab = "preview";
          break;
      }
    } catch (error) {
      console.error("Failed to get tab from search params", error);
    }

    // TODO - If there is a file in the search params, navigate to that... otherwise, navigate to the spec tab???
    navigate({
      to: "/codegen/$projectId/filesystem/$snapshotId",
      params: { projectId, snapshotId },
      search: { tab },
    });
  });

  const snapshots = useSortedSnapshots();

  const activeSnapshotLabel = useMemo(() => {
    const active = snapshots.find((s) => s.id === activeSnapshotId);
    return active ? getSnapshotLabel(active) : "snapshot";
  }, [snapshots, activeSnapshotId]);

  return (
    <FpDropdownMenu>
      <FpDropdownMenuTrigger asChild>
        <Button variant="secondary" size="sm">
          {activeSnapshotLabel}
          <ChevronDownIcon className="w-4 h-4 ml-1" />
        </Button>
      </FpDropdownMenuTrigger>
      <FpDropdownMenuContent align="end">
        {snapshots.map((s) => (
          <FpDropdownMenuItem
            key={s.id}
            onClick={() => onSnapshotSelect(s.id)}
            className="hover:bg-secondary hover:text-secondary-foreground focus:bg-secondary focus:text-secondary-foreground cursor-pointer"
          >
            <div className="flex items-center justify-between w-full">
              <span className="font-mono text-xs">{getSnapshotLabel(s)}</span>
              <div className="flex items-center gap-1 ml-2">
                {s.id === activeSnapshotId && (
                  <span className="text-xs">●</span>
                )}
              </div>
            </div>
          </FpDropdownMenuItem>
        ))}
      </FpDropdownMenuContent>
    </FpDropdownMenu>
  );
}

function getSnapshotLabel(s: UiSnapshotSummary) {
  if (
    typeof s.lamportId === "number" &&
    s.lamportId < Number.MAX_SAFE_INTEGER
  ) {
    return `v${s.lamportId}`;
  }

  if (s.label) {
    return s.label;
  }

  return s.id;
}
